import { useEffect, useState } from "react"
import PropTypes from "prop-types"

import {
  updateSubscriptionPlan,
  loadTierPrices,
  loadSubscriptionInterval,
} from "./api.tsx"
import Modal from "shared/components/modal"
import promiseTimeout from "shared/lib/timeout_promise.tsx"
import { captureMessage } from "@sentry/react"
import "./modal.scss"

import logIFTTTNextAction from "app/lib/ifttt_next_log_action"
import { saveUserPermissions } from "shared/scripts/new_badge_button.tsx"

const UpgradeToProModal = ({
  appletFeatureFlags,
  appletQuotaTotal,
  appletsInUse,
  currentTier,
  eligibleForTrial,
  isTrialing,
  onClose,
  onUpgrade,
  premiumService,
  proPlusProductId,
  showCheckoutModal,
  step,
  subscriptionPaymentType,
  tierRequirement,
  trackLocationType,
  trackObjectId,
  visible,
}) => {
  const upgradingExistingSubscription = currentTier !== "free"
  const canUpgrade = subscriptionPaymentType === "stripe"
  const [mobileWarning, setMobileWarning] = useState(false)
  const [loading, setLoading] = useState(false)
  const [proPrice, setProPrice] = useState("")
  const billingHeadsUp = upgradingExistingSubscription
    ? isTrialing
      ? `${proPrice}Continue your trial with Pro+ to get access to queries, filter code, and multiple accounts.`
      : `${proPrice}We'll bill you now, minus a credit for any remaining days on your current plan.`
    : ""

  let title
  let editAppletWarning
  let copy
  let targetPlanCopy
  let eligibleForTrialCopy = eligibleForTrial
    ? " You can trial Pro+ for 7 days."
    : ""
  let checkoutModalTarget = "pro"
  let mainCTALabel = eligibleForTrial ? "Try it free" : "Upgrade"
  const mainCTAstyle = eligibleForTrial ? "trial-button-width" : ""
  let objectId = `${step}_intercept`

  if (
    [
      "queries",
      "filter_code",
      "multi_service_account",
      "multi_action",
      "convert_pro_plus_applet",
    ].includes(step)
  ) {
    if (appletFeatureFlags.pro_features) {
      editAppletWarning = "This Applet uses Pro+ features. "
    } else if (appletFeatureFlags.intermediate_pro_features) {
      editAppletWarning = "This Applet uses Pro features. "
    }
  }

  useEffect(() => {
    if (upgradingExistingSubscription && visible && !proPrice) {
      setLoading(true)

      let promises
      let timeout = 1000
      promises = Promise.all([
        promiseTimeout(timeout, loadTierPrices()),
        promiseTimeout(
          timeout,
          loadSubscriptionInterval("/billing/plan_interval")
        ),
      ])

      promises
        .then(promiseValues => {
          const planInterval = promiseValues[1].plan_interval
          const proPrice = promiseValues[0][planInterval]

          setProPrice(
            `IFTTT Pro+ is billed at ${proPrice.formatted} per ${planInterval}. `
          )
        })
        .catch(() => {
          // Don't show the information for user but track on Sentry.
          captureMessage("UpgradeToProModal - Promise timeout", {
            extra: { timeout },
          })
        })
        .finally(() => {
          // Release upgrade button in any case
          setLoading(false)
        })
    }
  }, [visible])

  switch (step) {
    case "applet_quota":
      title = "Create more Applets"
      if (currentTier === "intermediate_pro" && isTrialing) {
        copy = `You have reached your ${appletQuotaTotal} Applet limit.`
      } else if (currentTier === "intermediate_pro") {
        copy = `You have reached your ${appletQuotaTotal} Applet limit. Upgrade now for unlimited Applets and get access to queries, filter code, and multiple accounts.`
      } else if (currentTier === "free") {
        eligibleForTrialCopy = ""
        copy = eligibleForTrial
          ? `You're using ${appletsInUse} of your ${appletQuotaTotal} free Applets. Start your 7-day IFTTT Pro trial to get up to 20 Applets, access to multiple actions, and faster speeds.`
          : `You're using ${appletsInUse} of your ${appletQuotaTotal} free Applets. Upgrade to IFTTT Pro to get up to 20 Applets, access to multiple actions, and faster speeds.`
        checkoutModalTarget = "intermediate_pro"
      }
      break
    case "default":
      copy =
        "Upgrade to get access to queries, filter code, and multiple accounts."
      break
    case "default_pro":
      title = "Upgrade to Pro"
      copy =
        "Upgrade to get up to 20 Applets, access to multiple actions, and faster speeds."
      checkoutModalTarget = "intermediate_pro"
      if (eligibleForTrialCopy) {
        eligibleForTrialCopy = " You can trial Pro for 7 days."
      }
      break
    case "convert_pro_plus_applet":
      copy =
        "Upgrade to get access to queries, filter code, and multiple accounts."
      break
    case "plan_upgrade":
      if (currentTier === "legacy_pro") {
        title = "Add multiple accounts"
        copy = "Upgrade to IFTTT Pro+ to add and manage more accounts."
      } else {
        title = "Unlimited Applets"
        copy =
          "Upgrade to get access to queries, filter code, and multiple accounts."
      }
      break
    case "action":
    case "trigger":
      checkoutModalTarget = tierRequirement
      targetPlanCopy =
        checkoutModalTarget === "intermediate_pro" ? "Pro" : "Pro+"
      title = `Add a ${targetPlanCopy} ${step}`
      copy = `Upgrade to IFTTT ${targetPlanCopy} to add this ${step} to your Applets.`
      eligibleForTrialCopy = eligibleForTrial ? ` You can trial ${targetPlanCopy} for 7 days.` : ""

      objectId = `${step}_${trackObjectId}_intercept`

      break
    case "applet_paid_features":
      checkoutModalTarget = tierRequirement
      targetPlanCopy =
        checkoutModalTarget === "intermediate_pro" ? "Pro" : "Pro+"
      title = `This Applet has one or more ${targetPlanCopy} features`
      copy = `Upgrade to IFTTT ${targetPlanCopy} to enable this Applet.`
      eligibleForTrialCopy = eligibleForTrial ? ` You can trial ${targetPlanCopy} for 7 days.` : ""

      break
    case "queries":
      title = "Add a query"
      copy = "Upgrade to IFTTT Pro+ to add queries to your Applets."

      break
    case "pro_service_tqas":
      title = `Connect to ${premiumService}`
      copy = `Upgrade to IFTTT Pro to access ${premiumService} triggers and actions.`
      eligibleForTrialCopy = eligibleForTrialCopy ? ` You can trial Pro for 7 days.` : ""

      break
    case "filter_code":
      title = "Add filter code"
      copy = "Upgrade to IFTTT Pro+ to add filter code to your Applets."

      break
    case "multi_service_account":
      title = "Add multiple accounts"
      if (
        currentTier === "legacy_pro" ||
        (currentTier === "intermediate_pro" && isTrialing)
      ) {
        copy = "Upgrade to IFTTT Pro+ to add and manage more accounts."
      } else {
        copy =
          "Upgrade to IFTTT Pro+ to add and manage more accounts. You will also get access to unlimited Applets and filter code."
      }
      break
    case "multi_action":
      title = "Add more actions"
      copy = "Upgrade to IFTTT Pro to add multiple actions to your Applets."
      checkoutModalTarget = "intermediate_pro"

      // User could be editing an Applet with multi_action + Pro+ feature (filter code, query, etc)
      if (appletFeatureFlags.pro_features) {
        copy = "Upgrade to IFTTT Pro+ to add multiple actions to your Applets."
        checkoutModalTarget = "pro"
      }

      if (eligibleForTrialCopy) {
        eligibleForTrialCopy = " You can trial Pro for 7 days."
      }

      break
    default:
      throw new Error(`Unknown step: ${step}`)
  }

  const handlePromptConfirmation = () => {
    logIFTTTNextAction("click", {
      object_type: "upgrade_to_pro_modal",
      object_id: objectId,
      location_type: trackLocationType,
    })

    onClose("showCheckoutModal")
    showCheckoutModal(checkoutModalTarget)
  }

  const handleProUpgrade = async ({ productId }) => {
    if (!canUpgrade) {
      setMobileWarning(true)

      logIFTTTNextAction("click", {
        object_type: "upgrade_to_pro_modal",
        object_id: objectId,
        location_type: trackLocationType,
      })
    } else {
      setLoading(true)

      logIFTTTNextAction("click", {
        object_type: "upgrade_to_pro_modal",
        object_id: `${objectId}_upgrade`,
        location_type: trackLocationType,
      })

      const { errors, user_subscription } = await updateSubscriptionPlan({
        productId,
      })
      setLoading(false)
      if (!errors?.length && user_subscription.plan_id == "pro") {
        window.appendFlash(
          "Upgrade complete. Welcome to IFTTT Pro+!",
          "success"
        )
        saveUserPermissions("pro", {
          filter_code: { permitted: true },
          multi_action: { permitted: true },
          queries: { permitted: true },
          multi_service_account: { permitted: true },
        })
        onUpgrade?.()
      } else {
        const errorMessage =
          errors[0]?.message ||
          "There was a problem upgrading your account. Please contact support for more information."
        window.appendFlash(errorMessage, "danger")
      }
      onClose()
    }
  }

  return (
    <div styleName="modal-container">
      <Modal show={visible} closeListener={onClose} includeCloseX={false}>
        {mobileWarning ? (
          <MobileWarning onClose={onClose} />
        ) : (
          <>
            <h2 className="mt0 mb0">{title}</h2>
            <p>
              {editAppletWarning}
              {copy}
              <br />
              {eligibleForTrialCopy}
            </p>
            {upgradingExistingSubscription && <p>{billingHeadsUp}</p>}
            <div className="action-buttons-container">
              {upgradingExistingSubscription ? (
                <button
                  className="button-tertiary"
                  styleName={mainCTAstyle}
                  disabled={loading}
                  onClick={() => handleProUpgrade({ productId: proPlusProductId })}
                >
                  {mainCTALabel}
                </button>
              ) : (
                <button className="button-tertiary" styleName={mainCTAstyle} onClick={() => handlePromptConfirmation()}>
                  {mainCTALabel}
                </button>
              )}
              <a className="link-standalone mt2" styleName="cancel-link" onClick={onClose}>
                Maybe later
              </a>
            </div>
          </>
        )}
      </Modal>
    </div>
  )
}

UpgradeToProModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  currentTier: PropTypes.oneOf([
    "free",
    "intermediate_pro",
    "legacy_pro",
    "pro",
  ]).isRequired,
  tierRequirement: PropTypes.oneOf(["free", "intermediate_pro", "pro"]),
  step: PropTypes.oneOf([
    "applet_quota",
    "trigger",
    "queries",
    "action",
    "filter_code",
    "multi_action",
    "multi_service_account",
    "plan_upgrade",
    "applet_paid_features",
    "pro_service_tqas",
    "default",
    "default_pro",
    "convert_pro_plus_applet",
  ]).isRequired,
  onClose: PropTypes.func,
  onUpgrade: PropTypes.func,
  appletsInUse: PropTypes.number,
  appletQuotaTotal: PropTypes.number,
  eligibleForTrial: PropTypes.bool.isRequired,
  premiumService: PropTypes.string,
  proPlusProductId: PropTypes.string.isRequired,
  subscriptionPaymentType: PropTypes.string,
  appletFeatureFlags: PropTypes.objectOf(PropTypes.bool),
  isTrialing: PropTypes.bool,
  trackLocationType: PropTypes.string.isRequired,
  trackObjectId: PropTypes.string,
  showCheckoutModal: PropTypes.func,
}

const MobileWarning = ({ onClose }) => {
  return (
    <>
      <h2>Upgrade with the IFTTT app</h2>
      <p>
        Your mobile subscription can&#39;t be updated on the web. You can
        upgrade your plan using the IFTTT app.
      </p>
      <div className="action-buttons-container">
        <button className="button-tertiary cancel-link" onClick={onClose}>
          Close
        </button>
      </div>
    </>
  )
}

MobileWarning.propTypes = {
  onClose: PropTypes.func.isRequired,
}

export default UpgradeToProModal
